import React, { useState, useEffect } from 'react';
import { Modal, FormGroup, FormControl, ControlLabel, Button, Glyphicon } from 'react-bootstrap';
import { TimePicker, DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from 'styled-components';
import { getMonitors, makeTimelapseRequest, estimateTimelapse } from '../utils/bedrockAPI';
import './TimelapseModal.css';
import { addDaysToDate } from '../utils/Common';
import LoaderButton from './LoaderButton';

const CustomTimePicker = styled(TimePicker)`
    & .MuiInputBase-input {
        font-family: var(--brand-font);
        color: var(--brand-slate);
        font-size: 24px;
        border: 1px solid var(--brand-border);
        padding: 5px;
        cursor: pointer;
        text-align: center;
        border-radius: var(--brand-radius);
    }
    
    & .MuiInputBase-input:hover {
        background-color: var(--brand-limestone);
    }
`;

const CustomDatePicker = styled(DatePicker)`
    & .MuiInputBase-input {
        font-family: var(--brand-font);
        color: var(--brand-slate);
        font-size: 24px;
        border: 1px solid var(--brand-border);
        padding: 5px;
        cursor: pointer;
        text-align: center;
        border-radius: var(--brand-radius);
    }
    
    & .MuiInputBase-input:hover {
        background-color: var(--brand-limestone);
    }
`;

const US_TIMEZONES = [
    'America/New_York',      // Eastern Time
    'America/Chicago',       // Central Time
    'America/Denver',        // Mountain Time
    'America/Los_Angeles',   // Pacific Time
    'America/Anchorage',     // Alaska Time
    'Pacific/Honolulu'       // Hawaii Time
];

const getTimezoneOffset = (timezone) => {
    const date = new Date();
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const offset = (tzDate - utcDate) / (1000 * 60 * 60);
    return Math.round(offset);
};

const getHighResMonitorId = (selectedMonitorId, allMonitors) => {
    if (!selectedMonitorId || !allMonitors) return null;
    
    // Find which group (low/medium/high) contains our selected monitor
    let selectedGroup = null;
    
    for(let view of Object.values(allMonitors)) {
        if(view.medium.monitorId === selectedMonitorId) {
            selectedGroup = view;
            break;
        }
    }

    if (!selectedGroup) return null;

    // Find the high resolution version of the same view
    return selectedGroup.high.monitorId;
};

const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
};

const isTomorrow = (date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.getDate() === tomorrow.getDate() &&
           date.getMonth() === tomorrow.getMonth() &&
           date.getFullYear() === tomorrow.getFullYear();
};

export default function TimelapseModal({ show, onHide, cap, monitorImages }) {
    const [startDate, setStartDate] = useState(addDaysToDate(new Date(),-14));
    const [endDate, setEndDate] = useState(new Date());
    const [startTime, setStartTime] = useState(new Date().setHours(7, 0, 0, 0));
    const [endTime, setEndTime] = useState(new Date().setHours(18, 0, 0, 0));
    const [selectedMonitor, setSelectedMonitor] = useState(null);
    const [timezone, setTimezone] = useState(() => {
        const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return US_TIMEZONES.includes(userTz) ? userTz : 'America/New_York';
    });
    const [name, setName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isEstimating, setIsEstimating] = useState(false);
    const [error, setError] = useState(null);
    const [showNameHelp, setShowNameHelp] = useState(false);
    const [showDateHelp, setShowDateHelp] = useState(false);
    const [showTimeHelp, setShowTimeHelp] = useState(false);
    const [showTimezoneHelp, setShowTimezoneHelp] = useState(false);
    const [showViewHelp, setShowViewHelp] = useState(false);
    const [allMonitors, setAllMonitors] = useState(null);
    const [estimate, setEstimate] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showTodayNote, setShowTodayNote] = useState(false);

    useEffect(() => {
        async function findMonitors() {
            const monitors = await getMonitors(cap.capId);
            setAllMonitors(monitors);
        }
        if(cap) {
            findMonitors();
        }
    }, [cap]);

    // Reopen with the form (and saved settings), not the previous success screen
    useEffect(() => {
        if (!show) return;
        setShowSuccess(false);
        setError(null);
        setEstimate(null);
        setIsLoading(false);
        setIsEstimating(false);
        setShowNameHelp(false);
        setShowDateHelp(false);
        setShowTimeHelp(false);
        setShowTimezoneHelp(false);
        setShowViewHelp(false);
    }, [show]);

    const handleSubmit = async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Prevent submission if start date is today
            if (isToday(startDate)) {
                setError('Unable to process timelapses from today until tomorrow. Please come back then and request the timelapse for today\'s date.');
                setIsLoading(false);
                return;
            }

            const highResMonitorId = getHighResMonitorId(selectedMonitor, allMonitors);
            if (!highResMonitorId) {
                setError('Could not find high resolution version of selected view');
                setIsLoading(false);
                return;
            }

            const request = {
                capId: cap.capId,
                monitorId: highResMonitorId,
                startDate: startDate.toISOString().split('T')[0]+"T00:00:00Z",
                endDate: endDate.toISOString().split('T')[0]+"T00:00:00Z",
                startHour: new Date(startTime).getHours(),
                endHour: new Date(endTime).getHours(),
                timeZone: getTimezoneOffset(timezone),
                name: name || `Timelapse ${cap.name}`
            };
            console.log(request);
            await makeTimelapseRequest(request);
            
            // Show success message
            setShowSuccess(true);
        } catch (err) {
            setError('Failed to create timelapse request. Please try again.');
            console.log(err);
        }
        setIsLoading(false);
    };

    const handleEstimate = async () => {
        setIsEstimating(true);
        setError(null);
        try {
            // Prevent estimation if start date is today
            if (isToday(startDate)) {
                setError('Unable to process timelapses from today until tomorrow. Please come back then and request the timelapse for today\'s date.');
                setIsEstimating(false);
                return;
            }

            const highResMonitorId = getHighResMonitorId(selectedMonitor, allMonitors);
            if (!highResMonitorId) {
                setError('Could not find high resolution version of selected view');
                setIsEstimating(false);
                return;
            }

            const estimate = await estimateTimelapse(
                highResMonitorId,
                startDate.toISOString().split('T')[0]+"T00:00:00Z",
                endDate.toISOString().split('T')[0]+"T00:00:00Z",
                new Date(startTime).getHours(),
                new Date(endTime).getHours(),
                getTimezoneOffset(timezone)
            );
            setEstimate(estimate);
        } catch (err) {
            setError('Failed to estimate timelapse. Please try again.');
            console.log(err);
        }
        setIsEstimating(false);
    };

    const formatTime = (minutes) => {
        if (minutes < 60) {
            return `${Math.round(minutes)} minutes`;
        }
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        return `${hours} hour${hours !== 1 ? 's' : ''}${remainingMinutes > 0 ? ` and ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}` : ''}`;
    };

    const formatLength = (seconds) => {
        if (seconds < 60) {
            return `${Math.round(seconds)} seconds`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.round(seconds % 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''}${remainingSeconds > 0 ? ` and ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}` : ''}`;
    };

    // Quick date option handlers
    const setToday = () => {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        setStartDate(today);
        setEndDate(tomorrow);
        setShowTodayNote(true);
    };

    const setYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const today = new Date();
        setStartDate(yesterday);
        setEndDate(today);
        setShowTodayNote(false);
    };

    const setLast7Days = () => {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setStartDate(sevenDaysAgo);
        setEndDate(tomorrow);
        setShowTodayNote(false);
    };

    const setLast30Days = () => {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setStartDate(thirtyDaysAgo);
        setEndDate(tomorrow);
        setShowTodayNote(false);
    };

    const setLast90Days = () => {
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setStartDate(ninetyDaysAgo);
        setEndDate(tomorrow);
        setShowTodayNote(false);
    };

    return (
        <Modal show={show} onHide={onHide} bsSize="large" enforceFocus={false} className="timelapse-modal">
            <Modal.Header closeButton>
                <div className="timelapse-modal-title">Request Timelapse</div>
            </Modal.Header>
            <Modal.Body>
                {showSuccess ? (
                    <div className="timelapse-modal-success">
                        <Glyphicon glyph="ok-circle" className="timelapse-modal-success-icon" />
                        <div className="timelapse-modal-success-title">
                            Timelapse Request Created Successfully!
                        </div>
                        <div className="timelapse-modal-success-text">
                            Bedrock will email you with a link to the timelapse when it's completed.
                        </div>
                    </div>
                ) : (
                    <FormGroup>
                        <div className="timelapse-label-row">
                            <ControlLabel>Timelapse Name</ControlLabel>
                            <Glyphicon
                                glyph="question-sign"
                                className="timelapse-help-icon"
                                onClick={() => setShowNameHelp(!showNameHelp)}
                            />
                        </div>
                        {showNameHelp && (
                            <div className="timelapse-modal-help">
                                Give your timelapse a descriptive name to help identify it later
                            </div>
                        )}
                        <FormControl
                            type="text"
                            placeholder={`Timelapse ${cap.name}`}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </FormGroup>
                )}

                {!showSuccess && (
                    <>
                    <FormGroup>
                        <div className="timelapse-label-row">
                            <ControlLabel>Date Range</ControlLabel>
                            <Glyphicon
                                glyph="question-sign"
                                className="timelapse-help-icon"
                                onClick={() => setShowDateHelp(!showDateHelp)}
                            />
                        </div>
                        {showDateHelp && (
                            <div className="timelapse-modal-help">
                                Select the start and end dates for your timelapse. The longer the range, the more time it will take to process. We recommend picking an end date that is the day after your desired end date. Use the quick options to process your timelapse for the period specified.
                            </div>
                        )}
                        <div className="timelapse-quick-dates">
                            <Button className="btn-brand-outline btn-compact" onClick={setToday}>Today</Button>
                            <Button className="btn-brand-outline btn-compact" onClick={setYesterday}>Yesterday</Button>
                            <Button className="btn-brand-outline btn-compact" onClick={setLast7Days}>Last 7 days</Button>
                            <Button className="btn-brand-outline btn-compact" onClick={setLast30Days}>Last 30 days</Button>
                            <Button className="btn-brand-outline btn-compact" onClick={setLast90Days}>Last 90 days</Button>
                        </div>
                        {showTodayNote && (
                            <div className="timelapse-modal-note timelapse-modal-note--warning">
                                <strong>Note:</strong> We're unable to process timelapses from today until tomorrow. Please come back then and request the timelapse for today's date.
                            </div>
                        )}
                        <div className="timelapse-picker-row">
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <CustomDatePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(date) => {
                                        setStartDate(date);
                                        if (isToday(date)) {
                                            setShowTodayNote(true);
                                        } else {
                                            setShowTodayNote(false);
                                        }
                                    }}
                                    format="MM/dd/yyyy"
                                    maxDate={addDaysToDate(new Date(), -1)}
                                    slot={{dialog:{disableEnforceFocus:true}}}
                                />
                                <CustomDatePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={setEndDate}
                                    format="MM/dd/yyyy"
                                    maxDate={addDaysToDate(new Date(), 1)}
                                    slot={{dialog:{disableEnforceFocus:true}}}
                                />
                            </MuiPickersUtilsProvider>
                        </div>
                        {(isToday(endDate) || isTomorrow(endDate)) && (
                            <div className="timelapse-modal-note timelapse-modal-note--info">
                                <strong>Note:</strong> The timelapse will be processed up until the current time (as those are the pictures that have been taken). If you want a timelapse for time periods in the future, please come back after that time and request it.
                            </div>
                        )}
                    </FormGroup>

                <FormGroup>
                    <div className="timelapse-label-row">
                        <ControlLabel>Time Range</ControlLabel>
                        <Glyphicon
                            glyph="question-sign"
                            className="timelapse-help-icon"
                            onClick={() => setShowTimeHelp(!showTimeHelp)}
                        />
                    </div>
                    {showTimeHelp && (
                        <div className="timelapse-modal-help">
                            Select the hours of the day you want to include in your timelapse. The timelapse will look better if you don't include night time hours.
                        </div>
                    )}
                    <div className="timelapse-picker-row">
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <CustomTimePicker
                                label="Start Time"
                                value={startTime}
                                onChange={setStartTime}
                                format="hh:mm a"
                                slot={{dialog:{disableEnforceFocus:true}}}
                            />
                            <CustomTimePicker
                                label="End Time"
                                value={endTime}
                                onChange={setEndTime}
                                format="hh:mm a"
                                slot={{dialog:{disableEnforceFocus:true}}}
                            />
                        </MuiPickersUtilsProvider>
                    </div>
                </FormGroup>

                <FormGroup>
                    <div className="timelapse-label-row">
                        <ControlLabel>Timezone</ControlLabel>
                        <Glyphicon
                            glyph="question-sign"
                            className="timelapse-help-icon"
                            onClick={() => setShowTimezoneHelp(!showTimezoneHelp)}
                        />
                    </div>
                    {showTimezoneHelp && (
                        <div className="timelapse-modal-help">
                            Select your local timezone to ensure the timelapse captures the correct hours of the day
                        </div>
                    )}
                    <FormControl
                        componentClass="select"
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                    >
                        {US_TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>
                                {tz.replace('America/', '').replace('Pacific/', '').replace(/_/g, ' ')}
                            </option>
                        ))}
                    </FormControl>
                </FormGroup>

                <FormGroup>
                    <div className="timelapse-label-row">
                        <ControlLabel>Select View</ControlLabel>
                        <Glyphicon
                            glyph="question-sign"
                            className="timelapse-help-icon"
                            onClick={() => setShowViewHelp(!showViewHelp)}
                        />
                    </div>
                    {showViewHelp && (
                        <div className="timelapse-modal-help">
                            Choose which camera view you want to use for the timelapse. We'll pull the highest quality images from the selected view.
                        </div>
                    )}
                    <div className={`monitor-grid ${cap.architecture && (cap.architecture.includes('4CAM') || cap.architecture.includes('MP4 JETSON BROMINE')) ? 'monitor-grid-2x2' : 'monitor-grid-1x4'}`}>
                        {monitorImages && Object.entries(monitorImages)
                            .filter(([monitorId, monitorData]) => {
                                if (cap.architecture && cap.architecture.includes('2CAM')) {
                                    return monitorData.id <= 2;
                                }
                                return true;
                            })
                            .map(([monitorId, monitorData]) => (
                                <div 
                                    key={monitorId}
                                    className={`monitor-option ${selectedMonitor === monitorId ? 'selected' : ''}`}
                                    onClick={() => setSelectedMonitor(monitorId)}
                                >
                                    <img src={monitorData.url} alt={`Monitor ${monitorData.id}`} />
                                    <div className="monitor-label">
                                        Monitor {monitorData.id}
                                    </div>
                                </div>
                            ))}
                    </div>
                </FormGroup>

                {selectedMonitor && (
                    <FormGroup>
                        <div className="timelapse-estimate-hint">
                            Click below to get an estimate of the time to produce the timelapse and the estimated length of the timelapse video
                        </div>
                        <LoaderButton
                            className="btn-brand-outline"
                            bsSize="large"
                            onClick={handleEstimate}
                            isLoading={isEstimating}
                            disabled={!selectedMonitor || isToday(startDate)}
                            block
                        >
                            Estimate Timelapse
                        </LoaderButton>
                        {estimate && (
                            <div className="timelapse-modal-estimate">
                                <div><strong>Estimated Processing Time:</strong> {formatTime(estimate.estimatedCompletionTime)}</div>
                                <div><strong>Estimated Timelapse Length:</strong> {formatLength(estimate.estimatedLength)}</div>
                                <div><strong>Number of Images:</strong> {estimate.count.toLocaleString()}</div>
                                <div className="timelapse-modal-estimate-disclaimer">
                                    These values are estimates and may vary based on the number of images and the length of the timelapse
                                </div>
                            </div>
                        )}
                    </FormGroup>
                )}

                {error && <div className="timelapse-modal-error">{error}</div>}
                </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {showSuccess ? (
                    <Button className="btn-brand-dark" onClick={onHide}>Close</Button>
                ) : (
                    <>
                        <Button className="btn-brand-outline" onClick={onHide}>Cancel</Button>
                        <Button
                            className="btn-brand-dark"
                            onClick={handleSubmit}
                            disabled={isLoading || !selectedMonitor || isToday(startDate)}
                        >
                            {isLoading ? 'Creating...' : 'Create Timelapse'}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
} 